import type { ElementType, ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";
import type { ContainerResponsiveValue } from "./contracts/responsive";
import { resolveGapClasses, type Space } from "./foundations/space";

const STACK_ALIGN_CLASSES = {
  stretch: "items-stretch",
  start: "items-start",
  center: "items-center",
  end: "items-end",
} as const;

type StackElement = "div" | "span" | "ul" | "ol" | "li" | "section" | "article" | "fieldset" | "nav";
export type StackAlign = keyof typeof STACK_ALIGN_CLASSES;

export type StackProps<TElement extends StackElement = "div"> = {
  children: ReactNode;
  gap?: ContainerResponsiveValue<Space>;
  align?: StackAlign;
} & DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

/** Vertical flow. Responsive gaps resolve against an explicit Container ancestor. */
export function Stack<TElement extends StackElement = "div">({
  align = "stretch",
  as,
  children,
  gap,
  ref,
  ...nativeProps
}: StackProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={twJoin("flex flex-col", STACK_ALIGN_CLASSES[align], resolveGapClasses(gap))}
    >
      {children}
    </Element>
  );
}
